import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import { confirmAlert } from 'react-confirm-alert';
import { useSelector } from 'react-redux';

import {
  approveCreationRequests,
  getCreationRequestLinks,
  overrideCreationRequest,
  stopCreationRequests,
  undoCreationRequest
} from '../../api/url_data_agent_evaluations';
import { ADMIN_SCOPES } from '../../constants/admin_scope_constants';
import { getPrettyPriceDisplay } from '../../helpers/formatting';
import { wait } from '../../helpers/helpers';
import {
  CREATION_LINK_STATE_OPTIONS,
  getApprovalMessage,
  getBatchModeLabel,
  getBatchTimeLeft,
  getCreationCountKey,
  getCreationReasonCopy,
  getCreationRequestErrorMessage,
  getLaneStopNote,
  isCreationLinkApprovable,
  replaceCreationLink
} from '../../helpers/url_data_agent_creation_requests_helpers';
import { formatDate, formatNumber } from '../../helpers/url_data_agent_evaluations_helpers';
import { getUrlDataLink } from '../../helpers/url_data_helpers';
import { hasScope } from '../../helpers/scope_helpers';
import { useInterval } from '../../hooks/useInterval';
import { useSearchParamsState } from '../../hooks/useSearchParamsState';
import ConfirmPrompt from '../General/ConfirmPrompt';
import CreationRequestRow from './CreationRequestRow';
import LiveMatchRowSkeleton from './LiveMatchRowSkeleton';
import SegmentedControl from './SegmentedControl';

import './SilentEvalCreationRequestLinks.scss';

const REFRESH_MS = 30000;
const BULK_CALL_PAUSE_MS = 1000;
const MAX_APPROVE_SELECTED = 500;
const URL_DEFAULTS = { batchState: 'ready' };

// One creation batch's links: where each stands, the product APM will create, and the buttons to approve, correct, stop or undo.
const SilentEvalCreationRequestLinks = ({ tag, active = true, onBack }) => {
  const [urlState, patchUrl] = useSearchParamsState(URL_DEFAULTS);
  const currentUser = useSelector(state => state.users.currentUser);
  const canChangeProducts = hasScope(currentUser?.scopes || [], ADMIN_SCOPES.EDIT_PRODUCT);
  const batchState = CREATION_LINK_STATE_OPTIONS.some(option => option.value === urlState.batchState) ? urlState.batchState : URL_DEFAULTS.batchState;
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [busyStems, setBusyStems] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [isBulkActing, setIsBulkActing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [copyingNoun, setCopyingNoun] = useState(null);
  const linkRereadsRef = useRef([]);
  const current = report?.tag === tag && report.batchState === batchState ? report : null;
  const rows = current?.rows || [];
  const counts = current?.links;
  const batch = current?.batch;
  const isIdle = !busyStems.size && !isBulkActing && !isFetching && !isLoadingMore;

  // Display data
  const stateOptions = CREATION_LINK_STATE_OPTIONS.map(option => ({
    value: option.value,
    label: counts ? `${option.label} ${formatNumber(option.value === 'all' ? counts.total : counts[getCreationCountKey(option.value)])}` : option.label
  }));
  const approvableRows = rows.filter(isCreationLinkApprovable);
  const selectedRows = approvableRows.filter(link => selectedIds.has(link.evaluation.id));
  const areAllSelected = approvableRows.length > 0 && selectedRows.length === approvableRows.length;
  const laneStopNote = getLaneStopNote(current?.lane);
  const timeLeft = current && getBatchTimeLeft(current);
  const canStop = Boolean(counts?.creating || batch?.createWhenReady);
  const isMoving = Boolean(counts?.waiting || counts?.beingRead || counts?.creating);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const withRereads = beginPageRead();
    setIsFetching(true);
    setError(null);
    getCreationRequestLinks(tag, { state: batchState })
      .then(data => {
        if (!cancelled) setReport(withRereads({ ...data, tag, batchState }));
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, tag, batchState, refreshKey]);

  // The counts follow a batch being read or created while it is on screen and nobody is acting on it; the rows wait for Refresh.
  useInterval(
    () => {
      if (!isIdle || document.visibilityState === 'hidden') return;
      getCreationRequestLinks(tag, { countOnly: 1 })
        .then(data => setReport(previous => (previous?.tag === tag ? { ...previous, ..._.omit(data, ['rows', 'total', 'hasMore', 'nextAfterId']) } : previous)))
        // A missed count is taken again on the next tick, and the page keeps the counts it has.
        .catch(() => {});
    },
    active && isMoving ? REFRESH_MS : null
  );

  const refresh = () => setRefreshKey(key => key + 1);

  // A page read's rows can be older than a link re-read that lands while it runs, so those re-reads are laid back over the page.
  const beginPageRead = () => {
    const rereadCount = linkRereadsRef.current.length;
    return page => linkRereadsRef.current.slice(rereadCount).reduce((next, link) => replaceCreationLink(next, link), page);
  };

  const loadMore = async () => {
    setIsLoadingMore(true);
    try {
      const data = await getCreationRequestLinks(tag, { state: batchState, afterId: current.nextAfterId });
      setReport(previous =>
        previous?.tag === tag && previous.batchState === batchState
          ? { ...previous, ...data, rows: [...previous.rows, ...data.rows], tag, batchState }
          : previous
      );
    } catch (err) {
      window.ALERT.error(`Could not load more links: ${err.message}`);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const placeLink = link => {
    linkRereadsRef.current.push(link);
    setReport(previous => (previous?.tag === tag ? replaceCreationLink(previous, link) : previous));
  };

  // Re-reads one link from the primary, which has the write an action just made.
  const reloadLink = async link => {
    try {
      const { rows: [fresh] } = await getCreationRequestLinks(tag, { hashedUrlStem: link.hashedUrlStem });
      if (fresh) placeLink(fresh);
    } catch (err) {
      window.ALERT.error(`Could not reload the link: ${err.message}`);
    }
  };

  // Actions. The link is re-read after every attempt: a teammate or APM may have moved it first.
  const runAction = async (link, work) => {
    if (busyStems.has(link.hashedUrlStem)) return;
    setBusyStems(stems => new Set(stems).add(link.hashedUrlStem));
    let fresh = null;
    try {
      fresh = (await work())?.link || null;
    } catch (err) {
      window.ALERT.error(getCreationRequestErrorMessage(err));
    } finally {
      if (fresh) placeLink(fresh);
      else await reloadLink(link);
      setBusyStems(stems => {
        const next = new Set(stems);
        next.delete(link.hashedUrlStem);
        return next;
      });
    }
  };

  const approveLink = async link => {
    const { approved } = await approveCreationRequests(tag, { evaluationIds: [link.evaluation.id] });
    if (!approved) window.ALERT.info('This link is no longer ready to approve.');
  };

  const confirmUndo = link =>
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header='Undo this creation?'
          subheader='The link and its pins are unmatched, and the link goes back to Links to Check when it can. The product, and any brand APM created for it, stay in the catalog, and this link cannot be requested again.'
          hideInput
          submitBtnDisplay='Undo'
          onCancel={onClose}
          onSubmit={() => {
            onClose();
            runAction(link, () => undoCreationRequest(tag, { evaluationId: link.evaluation.id }));
          }}
        />
      )
    });

  const handleAction = (link, key, edits = {}) => {
    const evaluationId = link.evaluation.id;
    if (key === 'approve') return runAction(link, () => approveLink(link));
    if (key === 'edit') return runAction(link, () => overrideCreationRequest(tag, { evaluationId, ...edits }));
    if (key === 'fix' || key === 'retry') return runAction(link, () => overrideCreationRequest(tag, { evaluationId, ...edits, approve: true }));
    if (key === 'undo') return confirmUndo(link);
    return undefined;
  };

  // Approve all and Stop make their later calls from this page, even after it goes back to all batches, so closing it part-way
  // through a Stop leaves the rest of the batch approved.
  const runBulkAction = async work => {
    setIsBulkActing(true);
    window.addEventListener('beforeunload', warnBeforeUnload);
    try {
      await work();
    } catch (err) {
      window.ALERT.error(getCreationRequestErrorMessage(err));
    } finally {
      window.removeEventListener('beforeunload', warnBeforeUnload);
      setIsBulkActing(false);
      setBulkProgress(null);
      setSelectedIds(new Set());
      refresh();
    }
  };

  const callUntilDone = async (callFrom, onCall) => {
    let result = await callFrom(0);
    onCall(result);
    while (result.nextAfterIntakeId != null) {
      await wait(BULK_CALL_PAUSE_MS);
      result = await callFrom(result.nextAfterIntakeId);
      onCall(result);
    }
  };

  const approveSelected = () =>
    runBulkAction(async () => {
      const result = await approveCreationRequests(tag, { evaluationIds: selectedRows.map(link => link.evaluation.id) });
      window.ALERT.success(getApprovalMessage(result));
    });

  // Approves what the count showed: links that turn ready after it wait for the next approval.
  const confirmApproveAll = () => {
    const { readyThroughCompletedAt } = current;
    const ready = formatNumber(counts.ready);
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header={`Approve all ${ready} ready ${counts.ready === 1 ? 'product' : 'products'}?`}
          subheader='APM creates each of them with you as the approver, at up to 10 a minute per catalog worker. Links that turn ready after this count wait for the next approval.'
          hideInput
          submitBtnDisplay={`Approve ${ready}`}
          onCancel={onClose}
          onSubmit={() => {
            onClose();
            approveAllReady(readyThroughCompletedAt);
          }}
        />
      )
    });
  };

  const approveAllReady = readyThroughCompletedAt =>
    runBulkAction(async () => {
      const totals = { approved: 0, noLongerReady: 0 };
      setBulkProgress({ action: 'approve', count: 0 });
      try {
        await callUntilDone(
          afterIntakeId => approveCreationRequests(tag, { all: true, readyThroughCompletedAt, afterIntakeId }),
          result => {
            totals.approved += result.approved;
            totals.noLongerReady += result.noLongerReady;
            setBulkProgress({ action: 'approve', count: totals.approved });
          }
        );
        window.ALERT.success(getApprovalMessage(totals));
      } catch (err) {
        const reason = _.trimEnd(getCreationRequestErrorMessage(err), '.');
        window.ALERT.error(`Could not approve every ready product: ${reason}. ${formatNumber(totals.approved)} approved so far; press Approve all ready for the rest.`);
      }
    });

  const confirmStop = () =>
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header='Stop creating this batch?'
          subheader='Approvals are withdrawn from every link APM has not created yet, and links read from now on arrive ready instead of approved. A creation already under way finishes. You can approve the links again later.'
          hideInput
          submitBtnDisplay='Stop creating'
          onCancel={onClose}
          onSubmit={() => {
            onClose();
            stopCreating();
          }}
        />
      )
    });

  const stopCreating = () =>
    runBulkAction(async () => {
      let stopped = 0;
      setBulkProgress({ action: 'stop', count: 0 });
      try {
        await callUntilDone(
          afterIntakeId => stopCreationRequests(tag, { afterIntakeId }),
          result => {
            stopped += result.stopped;
            setBulkProgress({ action: 'stop', count: stopped });
          }
        );
        window.ALERT.success(`Stopped. ${formatNumber(stopped)} approved ${stopped === 1 ? 'link waits' : 'links wait'} for a new approval.`);
      } catch (err) {
        const reason = _.trimEnd(getCreationRequestErrorMessage(err), '.');
        window.ALERT.error(`Could not withdraw every approval: ${reason}. ${formatNumber(stopped)} withdrawn so far; press Stop creating for the rest.`);
      }
    });

  const toggleSelected = link =>
    setSelectedIds(ids => {
      const next = new Set(ids);
      if (next.has(link.evaluation.id)) next.delete(link.evaluation.id);
      else next.add(link.evaluation.id);
      return next;
    });

  const toggleAllSelected = () => setSelectedIds(areAllSelected ? new Set() : new Set(approvableRows.map(link => link.evaluation.id)));

  // Copies a line for every link that needs a person, reading the pages the list has not loaded yet. The clipboard gets the read
  // as a promise within the click, because Safari and Firefox refuse a write that starts after the pages load.
  const copyNeedsPerson = async (toLine, noun) => {
    setCopyingNoun(noun);
    const lines = readNeedsPersonLines(toLine);
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ 'text/plain': lines.then(found => new Blob([found.join('\n')], { type: 'text/plain' })) })]);
      const copied = await lines;
      window.ALERT.success(`Copied ${formatNumber(copied.length)} ${copied.length === 1 ? noun : `${noun}s`}.`);
    } catch {
      const readError = await lines.then(() => null, err => err);
      window.ALERT.error(readError ? `Could not read every link to copy: ${readError.message}` : `Could not copy the ${noun}s.`);
    } finally {
      setCopyingNoun(null);
    }
  };

  const readNeedsPersonLines = async toLine => {
    const links = [...rows];
    let page = current;
    while (page.hasMore) {
      page = await getCreationRequestLinks(tag, { state: 'needs_person', afterId: page.nextAfterId });
      links.push(...page.rows);
    }
    return _.uniq(links.filter(link => link.state === 'needs_person').map(toLine).filter(Boolean));
  };

  return (
    <div className='silent-eval-creation-request-links'>
      <div className='se-toolbar'>
        <button type='button' className='se-btn ghost' onClick={onBack}>
          ← All batches
        </button>
        <SegmentedControl label='Link state' options={stateOptions} value={batchState} onChange={value => patchUrl({ batchState: value })} />
        <button type='button' className='se-btn' disabled={isFetching} onClick={refresh}>
          Refresh
        </button>
        <span className={cn('se-refreshing', { on: isFetching })} aria-hidden='true' />
      </div>

      {error && (
        <div className='crl-error' role='alert'>
          Could not {current ? 'refresh' : 'load'} this batch — {error}
        </div>
      )}
      {laneStopNote && (
        <div className='crl-lane-stopped' role='status'>
          {laneStopNote}
        </div>
      )}
      {batch && (
        <header className='crl-heading' aria-busy={isFetching}>
          <div className='crl-heading-main'>
            <p className='crl-eyebrow'>Creation batch</p>
            <h2>{batch.name}</h2>
            <p className='crl-facts'>
              <span>
                Started by <strong>{batch.requester?.name || 'an unknown user'}</strong> {formatDate(batch.firstParkedAt)}
              </span>
              <span className={cn('crl-mode', { 'create-when-ready': batch.createWhenReady })}>{getBatchModeLabel(batch)}</span>
              <span>
                {formatNumber(batch.links)} {batch.links === 1 ? 'link' : 'links'}
              </span>
            </p>
            {batch.sop && (
              <details className='crl-sop'>
                <summary>SOP</summary>
                <p>{batch.sop}</p>
              </details>
            )}
          </div>
          <p className='crl-summary'>
            <span>
              {formatNumber(current.pace.readLastHour)} read and {formatNumber(current.pace.createdLastHour)} created in the last hour
            </span>
            <span>{getPrettyPriceDisplay(current.spentUsd, { precision: 2 })} spent</span>
            {timeLeft && <span>{timeLeft}</span>}
          </p>
        </header>
      )}

      {current && (
        <div className='crl-actions'>
          {canChangeProducts && (
            <>
              {approvableRows.length > 0 && (
                <label className='crl-select-all'>
                  <input type='checkbox' checked={areAllSelected} disabled={isBulkActing} onChange={toggleAllSelected} />
                  Select all loaded
                </label>
              )}
              <button
                type='button'
                className='se-btn'
                disabled={!selectedRows.length || selectedRows.length > MAX_APPROVE_SELECTED || isBulkActing}
                title={selectedRows.length > MAX_APPROVE_SELECTED ? `Approve at most ${MAX_APPROVE_SELECTED} at a time, or approve all ready` : undefined}
                onClick={approveSelected}
              >
                Approve selected ({formatNumber(selectedRows.length)})
              </button>
              <button type='button' className='se-btn primary' disabled={!counts.ready || !current.readyThroughCompletedAt || isBulkActing} onClick={confirmApproveAll}>
                {bulkProgress?.action === 'approve' ? `Approving… ${formatNumber(bulkProgress.count)} so far` : `Approve all ready (${formatNumber(counts.ready)})`}
              </button>
              {canStop && (
                <button type='button' className='se-btn' disabled={isBulkActing} onClick={confirmStop}>
                  {bulkProgress?.action === 'stop' ? `Stopping… ${formatNumber(bulkProgress.count)} so far` : 'Stop creating'}
                </button>
              )}
            </>
          )}
          {batchState === 'needs_person' && (
            <>
              <button type='button' className='se-btn' disabled={!counts.needsPerson || Boolean(copyingNoun)} onClick={() => copyNeedsPerson(getUrlDataLink, 'link')}>
                {copyingNoun === 'link' ? 'Reading links…' : `Copy ${formatNumber(counts.needsPerson)} links`}
              </button>
              <button type='button' className='se-btn' disabled={!counts.needsPerson || Boolean(copyingNoun)} onClick={() => copyNeedsPerson(link => link.Pin_id, 'pin ID')}>
                {copyingNoun === 'pin ID' ? 'Reading pin IDs…' : `Copy ${formatNumber(counts.needsPerson)} pin IDs`}
              </button>
            </>
          )}
        </div>
      )}
      {batchState === 'needs_person' && current?.reasons.needsPerson.length > 0 && (
        <ul className='crl-reasons' aria-label='Why links need a person'>
          {current.reasons.needsPerson.map(({ reason, count }) => (
            <li key={reason}>
              <strong>{formatNumber(count)}</strong> {getCreationReasonCopy(reason)}
            </li>
          ))}
        </ul>
      )}

      {!current ? (
        !error && (
          <div className='crl-list'>
            {[0, 1, 2].map(index => (
              <LiveMatchRowSkeleton key={index} />
            ))}
          </div>
        )
      ) : !rows.length && !current.hasMore ? (
        <div className='se-empty'>{counts.total ? 'No links in this state.' : 'This batch has no links.'}</div>
      ) : (
        <div className='crl-list'>
          {rows.map(link => (
            <CreationRequestRow
              key={link.hashedUrlStem}
              link={link}
              lane={current.lane}
              isBusy={busyStems.has(link.hashedUrlStem) || isBulkActing}
              isSelected={Boolean(link.evaluation) && selectedIds.has(link.evaluation.id)}
              canChangeProducts={canChangeProducts}
              onToggleSelect={() => toggleSelected(link)}
              onAction={(key, edits) => handleAction(link, key, edits)}
            />
          ))}
          {current.hasMore && (
            <button type='button' className='se-btn crl-load-more' disabled={isLoadingMore} onClick={loadMore}>
              {isLoadingMore ? 'Loading…' : `Load more (${formatNumber(Math.max(current.total - rows.length, 0))} left)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const warnBeforeUnload = event => {
  event.preventDefault();
  event.returnValue = '';
};

SilentEvalCreationRequestLinks.propTypes = {
  tag: PropTypes.string.isRequired,
  active: PropTypes.bool,
  onBack: PropTypes.func.isRequired
};

export default SilentEvalCreationRequestLinks;
